// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { Clock, CheckCircle, AlertCircle, PlayCircle, ChevronRight } from 'lucide-react';

const STATUS_COLORS = {
  APPROVED:          { dot: '#10B981', label: 'Approved',          bg: '#D1FAE5' },
  IN_PROGRESS:       { dot: '#3B82F6', label: 'In Progress',        bg: '#EFF6FF' },
  AWAITING_APPROVAL: { dot: '#8B5CF6', label: 'Awaiting Approval',  bg: '#EDE9FE' },
  COMPLETED:         { dot: '#F59E0B', label: 'Completed',          bg: '#FEF3C7' },
  REJECTED:          { dot: '#EF4444', label: 'Rejected',           bg: '#FEE2E2' },
  OVERDUE:           { dot: '#DC2626', label: 'Overdue',            bg: '#FEE2E2' },
  CLEAN:             { dot: '#10B981', label: 'Clean',              bg: '#D1FAE5' },
};

export default function ZonesPage() {
  const navigate = useNavigate();
  const [zones, setZones]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [store, setStore]     = useState(null);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    const [{ data: storeData }, { data: zonesData }] = await Promise.all([
      supabase.from('grocery_stores').select('*').eq('active', true).limit(1).single(),
      supabase.from('grocery_zones').select('*').eq('active', true).order('zone_name'),
    ]);

    setStore(storeData);
    if (!zonesData) { setLoading(false); return; }

    // For each zone, get the latest session
    const zoneIds = zonesData.map(z => z.id);
    const { data: sessions } = await supabase
      .from('grocery_cleaning_sessions')
      .select('*')
      .in('zone_id', zoneIds)
      .order('created_at', { ascending: false });

    const latestByZone = {};
    (sessions || []).forEach(s => {
      if (!latestByZone[s.zone_id]) latestByZone[s.zone_id] = s;
    });

    // Determine zone status
    const now = new Date();
    const enriched = zonesData.map(z => {
      const latest = latestByZone[z.id];
      let status = 'CLEAN';

      if (latest) {
        if (['IN_PROGRESS', 'AWAITING_APPROVAL', 'COMPLETED', 'REJECTED'].includes(latest.session_status)) {
          status = latest.session_status;
        } else if (latest.session_status === 'APPROVED') {
          const hoursSince = (now - new Date(latest.approved_at)) / 3600000;
          status = hoursSince > z.cleaning_frequency_hours ? 'OVERDUE' : 'APPROVED';
        }
      } else {
        // Never cleaned
        status = 'OVERDUE';
      }

      return { ...z, latestSession: latest, status };
    });

    setZones(enriched);
    setLoading(false);
  };

  const startSession = async (zone) => {
    // Check if there's already an active session for this zone
    if (zone.latestSession && ['IN_PROGRESS', 'AWAITING_APPROVAL', 'COMPLETED'].includes(zone.latestSession.session_status)) {
      navigate(`/zones/${zone.id}/session`);
      return;
    }
    navigate(`/zones/${zone.id}/session`);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const overdueCount = zones.filter(z => z.status === 'OVERDUE').length;
  const cleanCount   = zones.filter(z => z.status === 'APPROVED').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Zone Map</h1>
          <p className="page-subtitle">{store?.name || 'Store'} — Select a zone to begin a cleaning session</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {overdueCount > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#FEE2E2', color: '#DC2626',
              padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            }}>
              <AlertCircle size={16} />
              {overdueCount} zone{overdueCount !== 1 ? 's' : ''} overdue
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#D1FAE5', color: '#059669',
            padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          }}>
            <CheckCircle size={16} />
            {cleanCount} clean
          </div>
        </div>
      </div>

      <div className="page-body">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}>
          {zones.map(zone => {
            const cfg = STATUS_COLORS[zone.status] || STATUS_COLORS.CLEAN;
            const isActive = ['IN_PROGRESS', 'AWAITING_APPROVAL', 'COMPLETED'].includes(zone.status);
            const isOverdue = zone.status === 'OVERDUE';

            return (
              <div
                key={zone.id}
                onClick={() => startSession(zone)}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 20,
                  border: `2px solid ${isOverdue ? '#FCA5A5' : isActive ? '#BFDBFE' : '#CCFBF1'}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  boxShadow: isOverdue ? '0 4px 14px rgba(239,68,68,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,118,110,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = isOverdue ? '0 4px 14px rgba(239,68,68,0.12)' : '0 1px 3px rgba(0,0,0,0.06)'; }}
              >
                {/* Zone icon + name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: zone.zone_color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 26, flexShrink: 0,
                  }}>
                    {zone.zone_icon || '🧹'}
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#134E4A' }}>{zone.zone_name}</div>
                    <div style={{ fontSize: 12, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      <Clock size={12} />
                      Every {zone.cleaning_frequency_hours}h
                      {zone.requires_supervisor_sign && (
                        <span style={{ marginLeft: 6, color: '#8B5CF6', fontWeight: 600 }}>· Supervisor req'd</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status badge */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: cfg.bg, borderRadius: 8, padding: '5px 10px',
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: cfg.dot }}>{cfg.label}</span>
                  </div>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: '#0F766E', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isActive ? <ChevronRight size={18} color="#fff" /> : <PlayCircle size={18} color="#fff" />}
                  </div>
                </div>

                {/* Last cleaned */}
                {zone.latestSession?.approved_at && (
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 10 }}>
                    Last approved: {new Date(zone.latestSession.approved_at).toLocaleString()}
                  </div>
                )}

                {isOverdue && !zone.latestSession && (
                  <div style={{ fontSize: 11, color: '#DC2626', marginTop: 10, fontWeight: 600 }}>
                    ⚠ Never cleaned — immediate action required
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
